import {
    ComponentKind,
    ComponentDescription,
    TableGlideType,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    getPrimitiveColumns,
} from "../description";
import { ComponentDescriptor, ComponentConfiguratorContext, ComponentBuilder } from ".";
import { ComponentHandlerBase } from "./handler";
import { ColumnPropertyHandler, ColumnPropertyFlag, EnumPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import { SerializedComponent } from "../../components/SerializedApp";
import { setColumnInRowForEditComponent, makeText } from "../builder-utils";
import { makeTextPropertyDescriptor } from "./descriptor-utils";

const ComponentKindRating: ComponentKind = "rating";

export function isRatingComponentDescription(x: ComponentDescription): x is RatingComponentDescription {
    return x.kind === ComponentKindRating;
}

const columnPropertyHandler = new ColumnPropertyHandler<ComponentDescription>(
    "dataColumnName",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.EditedInApp,
        ColumnPropertyFlag.DefaultCaption,
    ],
    undefined,
    ["rating", "stars"],
    getPrimitiveColumns,
    "number",
    PropertySection.Data
);

const maxRatingPropertyHandler = new EnumPropertyHandler(
    { maxRating: 3 },
    "Max Rating",
    "Max Rating",
    [
        {
            value: 3,
            label: "3",
        },
        {
            value: 4,
            label: "4",
        },
        {
            value: 5,
            label: "5",
        },
    ],
    PropertySection.Design,
    "slider"
);

const ratingUnitPropertyHandler = new EnumPropertyHandler(
    { ratingUnit: "stars" },
    "Unit",
    "Unit",
    [
        {
            value: "stars",
            label: "Stars",
            icon: "ratingStar",
        },
        {
            value: "hearts",
            label: "Hearts",
            icon: "ratingHeart",
        },
        {
            value: "money",
            label: "$",
            icon: "ratingMoney",
        },
        {
            value: "time",
            label: "Time",
            icon: "ratingTime",
        },
        {
            value: "pizzas",
            label: "Pizzas",
            icon: "ratingPizza",
        },
    ],
    PropertySection.Design,
    "dropdown"
);

interface RatingComponentDescription extends ComponentDescription {
    readonly valueProperty: PropertyDescription;
    readonly caption: PropertyDescription | undefined;
}

export class RatingComponentHandler extends ComponentHandlerBase<RatingComponentDescription> {
    constructor() {
        super(ComponentKindRating);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public get isNonEmptyValidationStopper(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: RatingComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        _searchVariable: SerializedComponent | undefined,
        _searchNotActive: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = columnPropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            b.getProperty(propertyName, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            b.getContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const maxRatingRaw = maxRatingPropertyHandler.getEnum(desc);
        const maxRating = b.constant(maxRatingRaw);

        const ratingUnitRaw = ratingUnitPropertyHandler.getEnum(desc);
        const ratingUnit = b.constant(ratingUnitRaw);

        return b.let(
            lets,
            b.ui("Rating", {
                value: valueToModify,
                onChange: action,
                isEnabled,
                validFlag: mutatingScreenKind !== undefined ? validFlag : undefined,
                caption: makeText(builder, desc.caption, tables.input, true),
                maxRating,
                ratingUnit,
            })
        );
    }

    public getDescriptor(
        _desc: RatingComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<RatingComponentDescription> {
        return {
            name: "Rating",
            description: "Rate an item",
            img: "rating",
            group: "Pickers",
            helpUrl: getDocURL("rating"),
            isLegacy: false,
            properties: [
                makeTextPropertyDescriptor(
                    "caption",
                    "Title",
                    "Enter the component's title",
                    false,
                    mutatingScreenKind,
                    {
                        propertySection: PropertySection.Top,
                        searchable: false,
                        emptyByDefault: true,
                        isCaption: "Rating",
                    }
                ),
                columnPropertyHandler,
                ratingUnitPropertyHandler,
                maxRatingPropertyHandler,
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }
}
